import React, { useMemo } from 'react';
import {
  VStack,
  Text,
  HStack,
  Button,
  Table,
} from '@adc/parallax-component-library';
import {
  IconPhosphorPlusRegular,
  IconPhosphorMinusRegular,
} from '@adc/parallax-icons/phosphor';
import { Dialog } from 'tamagui';
import { AccountWithRoleAssignments, Role } from '../../models';
import i18n from '../../utilities/i18n';
import './Modal.scss';

interface RoleAssignmentsConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedAccount: AccountWithRoleAssignments;
  availableRoles: Role[];
  selectedRoleKeys: string[];
  addRoleKeys: string[];
  removeRoleKeys: string[];
  isSaving?: boolean;
}

/**
 * Modal for confirming role assignments for a selected account
 * Shows a preview of current roles and roles after changes
 * @param {boolean} isOpen - Whether the modal is open
 * @param {() => void} onClose - Callback when modal closes
 * @param {() => void} onConfirm - Callback when changes are confirmed
 * @param {AccountWithRoleAssignments} selectedAccount - Selected account to assign roles to
 * @param {Role[]} availableRoles - Available roles to assign
 * @param {string[]} selectedRoleKeys - Selected role keys to apply
 * @param {string[]} addRoleKeys - Role keys to be added
 * @param {string[]} removeRoleKeys - Role keys to be removed
 * @returns {React.JSX.Element}
 */
export default function RoleAssignmentsConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  selectedAccount,
  availableRoles,
  selectedRoleKeys,
  addRoleKeys,
  removeRoleKeys,
  isSaving = false,
}: RoleAssignmentsConfirmationModalProps) {
  const { currentRoleNames, updatedRoleNames, newRoles, removedRoles } =
    useMemo(() => {
      const availableRoleNameByKey = new Map(
        availableRoles.map((role) => [role.key, role.name]),
      );
      const currentRoles = selectedAccount.roleAssignments.map(
        (roleAssignment) => roleAssignment.role,
      );
      const currentRoleNameByKey = new Map(
        currentRoles.map((role) => [role.key, role.name]),
      );
      const currentRoleKeys = currentRoles.map((role) => role.key);

      const resolveRoleNames = (
        roleKeys: string[],
        nameByKey: Map<string, string>,
      ) =>
        roleKeys
          .map((key) => nameByKey.get(key))
          .filter((name): name is string => Boolean(name));

      const updatedRoleNames = resolveRoleNames(
        selectedRoleKeys,
        availableRoleNameByKey,
      );
      const currentRoleNames = resolveRoleNames(
        currentRoleKeys,
        currentRoleNameByKey,
      );

      return {
        currentRoleNames,
        updatedRoleNames,
        newRoles: resolveRoleNames(addRoleKeys, availableRoleNameByKey),
        removedRoles: resolveRoleNames(removeRoleKeys, currentRoleNameByKey),
      };
    }, [
      selectedAccount.roleAssignments,
      selectedRoleKeys,
      availableRoles,
      addRoleKeys,
      removeRoleKeys,
    ]);

  const renderRoleList = (roleNames: string[]) => {
    if (roleNames.length === 0) {
      return (
        <Text type="$bodySmall.default" color="$text.60">
          {i18n.t('Common.none')}
        </Text>
      );
    }

    return (
      <VStack gap="$1" alignItems="flex-start">
        {roleNames.map((name) => {
          let roleColor: any;
          let RoleIcon: React.ElementType | null = null;

          if (newRoles.includes(name)) {
            roleColor = '$support.positive.default';
            RoleIcon = IconPhosphorPlusRegular;
          } else if (removedRoles.includes(name)) {
            roleColor = '$support.danger.default';
            RoleIcon = IconPhosphorMinusRegular;
          }

          return (
            <HStack key={name} gap="$1" alignItems="center">
              {RoleIcon ? <RoleIcon size="XS" color={roleColor} /> : null}
              <Text type="$bodySmall.default" color={roleColor}>
                {name}
              </Text>
            </HStack>
          );
        })}
      </VStack>
    );
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_RoleAssignmentsConfirmation.title')}
              </Text>
            </Dialog.Title>
          </VStack>

          <VStack
            gap="$2"
            paddingVertical="$4"
            flex={1}
            minHeight={0}
            overflow="hidden"
          >
            <Table
              aria-label={i18n.t('Modal_RoleAssignmentsConfirmation.title')}
              height="auto"
              minHeight={120}
              maxHeight="100%"
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {i18n.t('Modal_RoleAssignmentsConfirmation.name')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {i18n.t('Modal_RoleAssignmentsConfirmation.currentRoles')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {i18n.t('Modal_RoleAssignmentsConfirmation.updatedRoles')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row key={selectedAccount.key}>
                  <Table.TextCell>
                    <HStack testID="account-assignments-confirmation-account-name">
                      <Text type="$bodySmall.default">{`${selectedAccount.firstName} ${selectedAccount.lastName}`}</Text>
                    </HStack>
                  </Table.TextCell>
                  <Table.TextCell>
                    <VStack testID="account-assignments-confirmation-current-roles">
                      {renderRoleList(currentRoleNames)}
                    </VStack>
                  </Table.TextCell>
                  <Table.TextCell>
                    <VStack testID="account-assignments-confirmation-updated-roles">
                      {renderRoleList(updatedRoleNames)}
                    </VStack>
                  </Table.TextCell>
                </Table.Row>
              </Table.Body>
            </Table>
          </VStack>

          <HStack justifyContent="flex-end" gap="$2" flexShrink={0}>
            <Button
              variant="secondary"
              size="medium"
              label={i18n.t('Common.cancel')}
              onPress={onClose}
              disabled={isSaving}
              testID="account-assignments-confirmation-cancel-button"
            />
            <Button
              variant="primary"
              size="medium"
              label={i18n.t('Common.save')}
              onPress={onConfirm}
              disabled={isSaving}
              testID="account-assignments-confirmation-confirm-button"
            />
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
